import * as React from 'react';
import {ThemeProvider} from 'styled-components';
import {GlobalStyle} from './global';
import {darkTheme, theme} from './theme';
import {
  applyThemeToDocument,
  getSavedThemePreference,
  getSystemTheme,
  resolveThemePreference,
  saveThemePreference,
  ThemePreference,
} from './theme-preference';

type ThemeModeContextValue = {
  preference: ThemePreference;
  resolvedTheme: 'light' | 'dark';
  setPreference: (preference: ThemePreference) => void;
};

const ThemeModeContext = React.createContext<ThemeModeContextValue | null>(
  null,
);

const getDarkModeMedia = () =>
  typeof window === 'undefined' || !window.matchMedia
    ? undefined
    : window.matchMedia('(prefers-color-scheme: dark)');

export const useThemeMode = () => {
  const context = React.useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within StyleProvider');
  }
  return context;
};

export const StyleProvider = ({children}: {children: React.ReactNode}) => {
  const [preference, setPreferenceState] = React.useState<ThemePreference>(() =>
    getSavedThemePreference(
      typeof window === 'undefined' ? undefined : window.localStorage,
    ),
  );
  const [systemTheme, setSystemTheme] = React.useState<'light' | 'dark'>(() =>
    getSystemTheme(getDarkModeMedia()),
  );

  React.useEffect(() => {
    const media = getDarkModeMedia();
    if (!media) return;

    const updateSystemTheme = () => setSystemTheme(getSystemTheme(media));
    if (media.addEventListener) {
      media.addEventListener('change', updateSystemTheme);
    } else {
      media.addListener?.(updateSystemTheme);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', updateSystemTheme);
      } else {
        media.removeListener?.(updateSystemTheme);
      }
    };
  }, []);

  const resolvedTheme = resolveThemePreference(preference, systemTheme);

  React.useEffect(() => {
    applyThemeToDocument(
      resolvedTheme,
      typeof document === 'undefined' ? undefined : document.documentElement,
    );
  }, [resolvedTheme]);

  const setPreference = React.useCallback((nextPreference: ThemePreference) => {
    setPreferenceState(nextPreference);
    saveThemePreference(
      typeof window === 'undefined' ? undefined : window.localStorage,
      nextPreference,
    );
  }, []);

  const contextValue = React.useMemo(
    () => ({preference, resolvedTheme, setPreference}),
    [preference, resolvedTheme, setPreference],
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={resolvedTheme === 'dark' ? darkTheme : theme}>
        <GlobalStyle />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
